import { PLATFORM_ID, Inject, Injectable } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as Constants from './ft-device-detector.constants';
import { ReTree } from './retree';

export interface DeviceInfo {
  userAgent: string;
  os: string;
  browser: string;
  device: string;
  os_version: string;
  browser_version: string;
}

export interface DeviceInfoNew {
  browser: string;
  browserVersion: string;
  deviceId: any;
  deviceType: string;
  osName: string;
  osVersion: string;
  channel: string;
  trusted: boolean;
  options: any;
}

@Injectable({
  providedIn: 'root'
})
export class FTDeviceDetectorService {
  ua = '';
  userAgent = '';
  os = Constants.OS.UNKNOWN;
  browser = Constants.BROWSERS.UNKNOWN;
  device = Constants.DEVICES.UNKNOWN;
  osVersion = '0';
  browserVersion = '0';
  reTree = new ReTree();
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.userAgent = window.navigator.userAgent;
    }
    this._setDeviceInfo(this.userAgent);
    this.getBrowserInfo(this.userAgent);
  }

  /**
   * @desc Sets the initial value of the device when the service is initiated.
   * This value is later accessible for usage.
   */
  private _setDeviceInfo(ua = this.userAgent): void {
    if (ua !== this.userAgent) {
      this.userAgent = ua;
    }

    const isIos13Ipad = this.isBrowser && typeof navigator !== 'undefined' && (
      this.reTree.test(this.userAgent, Constants.TABLETS_RE.iPad) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );

    const osMatches = this.detectMatches(Constants.OS, Constants.OS_RE, ua);
    const browserMatches = this.detectMatches(Constants.BROWSERS, Constants.BROWSERS_RE, ua);
    const osVersionMatches = this.detectMatches(Constants.OS_VERSIONS, Constants.OS_VERSIONS_RE, ua);
    const deviceMatches = this.detectMatches(Constants.DEVICES, Constants.DEVICES_RE, ua, {
      overrideKey: Constants.DEVICES.iPad,
      overrideValue: isIos13Ipad
    });

    this.os = this.resolveMatch(Constants.OS, osMatches, Constants.OS.UNKNOWN);
    this.browser = this.resolveMatch(Constants.BROWSERS, browserMatches, Constants.BROWSERS.UNKNOWN);
    this.osVersion = this.resolveMatch(Constants.OS_VERSIONS, osVersionMatches, Constants.OS_VERSIONS.UNKNOWN);
    this.device = this.resolveMatch(Constants.DEVICES, deviceMatches, Constants.DEVICES.UNKNOWN, Constants.DEVICES.ANDROID);
    this.browserVersion = this.extractBrowserVersion(ua, this.browser);
  }

  private detectMatches(source: Record<string, string>, regexMap: Record<string, any>, ua: string, override?: { overrideKey: string; overrideValue: boolean }): Record<string, boolean> {
    const matches: Record<string, boolean> = {};

    for (const key of Object.keys(source)) {
      const value = source[key];
      if (override?.overrideKey === value) {
        matches[value] = override.overrideValue;
        continue;
      }

      matches[value] = this.reTree.test(ua, regexMap[key]);
    }

    return matches;
  }

  private resolveMatch(source: Record<string, string>, matches: Record<string, boolean>, unknownValue: string, androidFallback?: string): string {
    let result = unknownValue;

    for (const key of Object.keys(source)) {
      const value = source[key];
      const isMatch = matches[value];

      if (result === androidFallback && isMatch) {
        result = value;
        break;
      }

      if (result === unknownValue && isMatch) {
        result = value;
        if (!androidFallback) {
          break;
        }
      }
    }

    return result;
  }

  private extractBrowserVersion(userAgent: string, browser: string): string {
    const versionRegex = Constants.BROWSER_VERSIONS_RE[browser];
    if (!versionRegex) {
      return '0';
    }

    const execRegex = (regex: RegExp): string | null => {
      const result = this.reTree.exec(userAgent, regex);
      return result?.[1] ?? null;
    };

    if (Array.isArray(versionRegex)) {
      for (const regex of versionRegex) {
        const version = execRegex(regex);
        if (version) {
          return version;
        }
      }
      return '0';
    }

    return execRegex(versionRegex) ?? '0';
  }

  /**
   * @desc Returns the device information
   * @returns the device information object.
   */
  public getDeviceInfo(): DeviceInfoNew {
    return {
      browser: this.browser,
      browserVersion: this.browserVersion,
      deviceId: null,
      deviceType: this.device,
      osName: this.os,
      osVersion: this.osVersion,
      channel: 'web',
      trusted: false,
      options: null
    };
  }

  /**
   * @desc Compares the current device info with the mobile devices to check
   * if the current device is a mobile and also check current device is tablet so it will return false.
   * @returns whether the current device is a mobile
   */
  public isMobile(userAgent = this.userAgent): boolean {
    if (this.isTablet(userAgent)) {
      return false;
    }

    return Object.values(Constants.MOBILES_RE).some((regex) => this.reTree.test(userAgent, regex));
  }

  /**
   * @desc Compares the current device info with the tablet devices to check
   * if the current device is a tablet.
   * @returns whether the current device is a tablet
   */
  public isTablet(userAgent = this.userAgent): boolean {
    if (this.isBrowser && typeof navigator !== 'undefined') {
      if (this.reTree.test(this.userAgent, Constants.TABLETS_RE.iPad) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
        return true;
      }
    }

    return Object.values(Constants.TABLETS_RE).some((regex) => this.reTree.test(userAgent, regex));
  }

  /**
   * @desc Compares the current device info with the desktop devices to check
   * if the current device is a desktop device.
   * @returns whether the current device is a desktop device
   */
  public isDesktop(userAgent = this.userAgent): boolean {
    if (this.device === Constants.DEVICES.UNKNOWN) {
      if (this.isMobile(userAgent) || this.isTablet(userAgent)) {
        return false;
      }
    }

    return [Constants.DEVICES.CHROME_BOOK, Constants.DEVICES.UNKNOWN].includes(this.device);
  }

  getBrowserInfo(userAgent: string): void {
    const unknown = 'Unknown';
    const nVer = typeof navigator !== 'undefined' ? navigator.appVersion : '';

    let os = unknown;
    const clientStrings = [
      { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
      { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
      { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
      { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
      { s: 'Android', r: /Android/ },
      { s: 'iOS', r: /(iPhone|iPad|iPod)/ },
      { s: 'Mac OS X', r: /Mac OS X/ },
      { s: 'Linux', r: /(Linux|X11)/ }
    ];

    for (const { s, r } of clientStrings) {
      if (userAgent && r.test(userAgent)) {
        os = s;
        break;
      }
    }

    let osVersion = unknown;
    if (os.startsWith('Windows')) {
      const match = /Windows (.*)/.exec(os);
      osVersion = match?.[1] ?? unknown;
      os = 'Windows';
    }

    switch (os) {
      case 'Mac OS X': {
        const match = /Mac OS X (10[\._\d]+)/.exec(userAgent);
        osVersion = match?.[1] ?? unknown;
        break;
      }
      case 'Android': {
        const match = /Android ([\._\d]+)/.exec(userAgent);
        osVersion = match?.[1] ?? unknown;
        break;
      }
      case 'iOS': {
        const match = /OS (\d+)_(\d+)_(\d+)?/.exec(nVer);
        if (match) {
          osVersion = `${match[1]}.${match[2]}.${match[3] ?? '0'}`;
        }
        break;
      }
    }

    this.os = os;
    this.osVersion = osVersion;
  }
}
